import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { Icon } from '@codex-brightworld/icons';

const Indeterminate = styled.div`
  background-color: ${({ theme }) => theme.colors.neutral.white};
  width: 16px;
  height:2px;
  justify-self: center;
  align-self: center;
`;

const Box = styled.div`
  box-sizing: border-box;
  margin-right: 10px;
  padding: 3px;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  display: flex;

  ${({
    theme,
    checked, disabled, size
  }) => css`
    border: 1px solid ${theme.colors.neutral.grey[4]};
    background-color: ${theme.colors.neutral.white};

    ${Icon} {
      svg path:last-child {
        fill: ${theme.colors.neutral.white};
      }
    }

    ${checked && css`
      border-color: ${theme.colors.primary.purple} !important;
      background-color: ${theme.colors.primary.purple} !important;
    `}

    ${disabled && css`
      border-color: ${theme.colors.neutral.grey[4]} !important;
      background-color: ${theme.colors.neutral.grey[2]} !important;
      ${Indeterminate} {
        background-color: ${theme.colors.neutral.grey[4]} !important;
      }
    `}

    ${size === 'small' && css`
      width: 16px;
      height: 16px;

      ${Icon} {
        width: 11px;
        height: 11px;
        position: relative;
        top: -4px;
      }

      ${Indeterminate} {
        width: 10px;
      }
    `}
  `};
`;

const Input = styled.input.attrs({ type: 'checkbox', readOnly: true })`
  display: none;

  ${({ checked }) => !checked && css`
    & + ${Icon} {
      visibility: hidden;
    }
  `};
`;

const Label = styled.div`
  ${({ disabled, theme }) => disabled && css`
    color: ${theme.colors.shades[5]};
  `}

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    flex: 1 0 auto;
  }
`;

const Container = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  background-color: transparent;
  cursor: pointer;
  ${({
    centered
  }) => css`${centered === true && 'justify-content: center;'}`}

  ${({
    theme
  }) => css`
    padding: ${theme.spacing[2]} ${theme.spacing[4]};
  `};

  &:hover {
    ${Box} {
      background-color: ${({ theme }) => theme.colors.semantic.lightPurple};
      box-shadow: 0 0 6px 0 rgba(33, 48, 56, 0.2);
    }
   }
`;

/**
 * @tags styled, checkbox, tick, form field
 */
const Checkbox = props => (
  <Container
    onClick={props.disabled ? undefined : props.onCheck}
    {...props.attrs.container}
    data-qa={props.qa}
    centered={props.centered}
    className={props.className}
  >
    <Box checked={props.checked} size={props.size} disabled={props.disabled} {...props.attrs.box}>
      <Input checked={props.checked} {...props.attrs.input} />
      {props.indeterminate ? <Indeterminate /> : <Icon icon="tick" />}
    </Box>

    {props.children && (
      <Label disabled={props.disabled}>
        {props.children}
      </Label>
    )}
  </Container>
);

Checkbox.propTypes = {
  attrs: PropTypes.shape({
    container: PropTypes.shape({}),
    box: PropTypes.shape({}),
    input: PropTypes.shape({})
  }),
  children: PropTypes.node,
  checked: PropTypes.bool,
  disabled: PropTypes.bool,
  onCheck: PropTypes.func,
  indeterminate: PropTypes.bool,
  size: PropTypes.oneOf([
    'normal',
    'small'
  ]),
  qa: PropTypes.string,
  centered: PropTypes.bool,
  className: PropTypes.string
};

Checkbox.defaultProps = {
  attrs: {
    container: {},
    box: {},
    input: {}
  },
  children: undefined,
  checked: false,
  size: 'normal',
  disabled: false,
  indeterminate: false,
  onCheck: () => {},
  qa: undefined,
  centered: false,
  className: undefined
};

/** @component */
export default Checkbox;
